import React, { useMemo, useState, useEffect } from 'react';
import { Button, Modal, Table } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  Label,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { useTheme } from '../../contexts/useTheme';
import { useChartAnimations } from '../../contexts/useChartAnimations';
import { useObservationFields } from '../../hooks/useObservationFields';
import type { StationData } from '../../api/stationObservationsService';
import { circuitsService } from '../../api/circuitsService';
import {
  shouldDownsample,
  getBucketStarts,
  getAutoBucketMs,
} from '../../utils/observationDownsampling';
import './StationCharts.css';

// GeoJSON types for circuit rendering
interface GeoJSONGeometry {
  type: string;
  coordinates: number[] | number[][] | number[][][];
}

interface GeoJSONFeature {
  type: string;
  geometry: GeoJSONGeometry;
  properties?: Record<string, unknown>;
}

interface GeoJSONFeatureCollection {
  type: string;
  features: GeoJSONFeature[];
}

/** Projection data for converting geographic coords to SVG space */
interface CircuitProjection {
  minLng: number;
  maxLat: number;
  lngScale: number;
  maxDim: number;
  svgWidth: number;
  svgHeight: number;
}

/**
 * Converts GeoJSON coordinates to SVG path for circuit background, preserving
 * the natural aspect ratio with latitude correction. Returns projection data
 * so the view can be centered on a station.
 */
const geojsonToSVGPathAndBounds = (
  geojson: GeoJSONFeatureCollection
): { path: string; viewBox: string; projection: CircuitProjection } | null => {
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    return null;
  }

  const trackFeature = geojson.features.find(
    (f) => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
  );

  if (!trackFeature) {
    return null;
  }

  const coordinates: number[][] = trackFeature.geometry.type === 'LineString'
    ? (trackFeature.geometry.coordinates as number[][])
    : (trackFeature.geometry.coordinates as number[][][])[0];

  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  coordinates.forEach((coord) => {
    minLng = Math.min(minLng, coord[0]);
    minLat = Math.min(minLat, coord[1]);
    maxLng = Math.max(maxLng, coord[0]);
    maxLat = Math.max(maxLat, coord[1]);
  });

  // Apply latitude correction for proportional rendering
  const midLat = (minLat + maxLat) / 2;
  const lngScale = Math.cos((midLat * Math.PI) / 180);

  const geoWidth = (maxLng - minLng) * lngScale;
  const geoHeight = maxLat - minLat;
  const maxDim = Math.max(geoWidth, geoHeight);
  if (maxDim === 0) return null;

  const svgWidth = (geoWidth / maxDim) * 100;
  const svgHeight = (geoHeight / maxDim) * 100;

  // Add padding (10% on each side)
  const pad = 0.1;
  const padX = svgWidth * pad;
  const padY = svgHeight * pad;
  const viewBox = `${-padX} ${-padY} ${svgWidth + padX * 2} ${svgHeight + padY * 2}`;

  const path = coordinates.map((coord, i: number) => {
    const x = (coord[0] - minLng) * lngScale / maxDim * 100;
    const y = (maxLat - coord[1]) / maxDim * 100;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');

  return { path, viewBox, projection: { minLng, maxLat, lngScale, maxDim, svgWidth, svgHeight } };
};

interface StationChartsProps {
  station: StationData;
  seasonYear: number;
  circuitId: string;
  geojsonId?: string | null; // Optional custom GeoJSON file ID (defaults to circuitId if not set)
  selectedStartDate: Date;
  selectedEndDate: Date;
  circuitTimeZone?: string; // IANA timezone for displaying timestamps in circuit local time
  /** Station position (WGS84) to center the wind-direction track layer on this WS */
  stationLat?: number;
  stationLon?: number;
  sessions?: Array<{ start_time: string; end_time: string; session_name: string }>;
  /** Controlled detail modal (when provided, modal is opened from parent e.g. tab header icon) */
  detailModalOpen?: boolean;
  onDetailModalClose?: () => void;
  /** When true, hides legend, stats (Min/Max/Avg), and wind speed color legends below charts */
  hideLegend?: boolean;
  /** Chart height in pixels. Default 250. Use smaller values for compact/stacked views. */
  chartHeight?: number;
  /** When provided, only the listed charts are rendered. Default: all charts visible. */
  visibleCharts?: ('temperature' | 'wind' | 'humidity-pressure' | 'rain' | 'wind-direction')[];
  /** Force-disable chart animations regardless of global setting. Useful in resizable containers. */
  disableAnimations?: boolean;
}

interface ChartDataPoint {
  timestamp: string;
  time: string;
  date: string;
  dateTimeLabel: string; // Combined label for x-axis (date above, time below) - used for session ReferenceArea
  airTemp?: number | null;
  trackTemp?: number | null;
  windSpeed?: number | null;
  gusts?: number | null;
  humidity?: number | null;
  pressure?: number | null;
  rain?: number | null;
  windDirection?: number | null;
  windArrowY?: number; // Fixed Y position for wind direction arrows
}

interface WindDirectionData {
  direction: string;
  percentage: number;
  count: number;
  // Stacked percentages by speed category
  speed0to5: number;    // < 5 km/h
  speed5to10: number;   // 5-10 km/h
  speed10to15: number;  // 10-15 km/h
  speed15to20: number;  // 15-20 km/h
  speed20plus: number;  // > 20 km/h
}

// Wind speed color scale (matches legacy system)
const windSpeedColors = [
  { max: 5, color: '#00CED1', label: '< 5 km/h' },      // Cyan
  { max: 10, color: '#32CD32', label: '5-10 km/h' },    // Green
  { max: 15, color: '#FFD700', label: '10-15 km/h' },   // Yellow
  { max: 20, color: '#FFA500', label: '15-20 km/h' },   // Orange
  { max: Infinity, color: '#FF4444', label: '> 20 km/h' }, // Red
];

const getWindSpeedColor = (speed: number | null | undefined): string => {
  if (speed === null || speed === undefined) return '#666';
  for (const { max, color } of windSpeedColors) {
    if (speed < max) return color;
  }
  return '#FF4444';
};

// Custom arrow shape for wind direction on the wind chart
interface WindArrowProps {
  cx?: number;
  cy?: number;
  payload?: ChartDataPoint;
  fill?: string;
}

const WindArrow: React.FC<WindArrowProps> = ({ cx, cy, payload }) => {
  if (!cx || !cy || !payload?.windDirection) return null;
  
  // Wind direction is where the wind is coming FROM, so we add 180° to show where it's going
  // SVG rotation: 0° = pointing right, so we adjust: North (0°) should point up
  const rotation = (payload.windDirection + 180) % 360;
  
  // Color based on wind speed
  const arrowColor = getWindSpeedColor(payload.windSpeed);
  
  return (
    <g transform={`translate(${cx}, ${cy}) rotate(${rotation})`}>
      <path
        d="M0,-6 L3,4 L0,2 L-3,4 Z"
        fill={arrowColor}
        stroke="#333"
        strokeWidth={0.5}
      />
    </g>
  );
};

// Custom tooltip for wind direction chart: shows per-band % (not cumulative) and sorts by value
const WindDirectionTooltip = ({
  active,
  payload,
  label,
  isDark,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ name?: React.ReactNode; value?: unknown }>;
  label?: React.ReactNode;
  isDark: boolean;
}) => {
  const directionLabel = label != null ? String(label) : '';
  if (!active || !payload?.length || !directionLabel) return null;

  const cumulativeOrder = ['< 5 km/h', '5-10 km/h', '10-15 km/h', '15-20 km/h', '> 20 km/h'];
  const valuesByName: Record<string, number> = {};
  payload.forEach((p) => {
    valuesByName[String(p.name ?? '')] = typeof p.value === 'number' ? p.value : 0;
  });

  const cumulatives = cumulativeOrder.map((name) => valuesByName[name] ?? 0);
  const perBand = cumulativeOrder.map((name, i) => ({
    label: name,
    color: windSpeedColors[i].color,
    value: i === 0 ? cumulatives[0] : cumulatives[i] - cumulatives[i - 1],
  }));

  const perBandOrdered = perBand; // Already in speed category order (< 5, 5-10, 10-15, 15-20, > 20)
  const total = perBand.reduce((sum, p) => sum + p.value, 0);
  const rows = perBandOrdered.map((p) => ({
    ...p,
    pctOfDirection: total > 0 ? (p.value / total) * 100 : 0,
  }));

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 10,
    color: isDark ? 'rgba(255,255,255,0.7)' : '#666',
    padding: '2px 8px 4px 0',
    borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}`,
  };
  const tdStyle: React.CSSProperties = {
    fontSize: 12,
    padding: '2px 8px 4px 0',
    color: isDark ? 'rgba(255,255,255,0.9)' : '#333',
    borderBottom: `1px solid ${isDark ? 'rgba(68,68,68,0.5)' : 'rgba(0,0,0,0.06)'}`,
  };

  return (
    <div
      style={{
        backgroundColor: isDark ? '#1f1f1f' : '#fff',
        border: `1px solid ${isDark ? '#444' : '#ddd'}`,
        borderRadius: 4,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, color: isDark ? '#fff' : '#000' }}>
        {directionLabel} — {total.toFixed(1)}% of all observations
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={thStyle}>Speed</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>% of all</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>% of dir.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label: bandLabel, color, value, pctOfDirection }) => (
            <tr key={bandLabel}>
              <td style={tdStyle}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: color, flexShrink: 0 }} />
                  {bandLabel}
                </span>
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{value.toFixed(1)}%</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{pctOfDirection.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Custom tick for multi-line x-axis labels (date above, time below)
const CustomXAxisTick = ({ x, y, payload, textColor }: { x: string | number; y: string | number; payload: { value: string }; textColor: string }) => {
  const [date, time] = payload.value.split('\n');
  const tickX = Number(x);
  const tickY = Number(y);
  return (
    <g transform={`translate(${tickX},${tickY + 8})`}>
      <text x={0} y={0} textAnchor="middle" fill={textColor} fontSize={10}>
        <tspan x={0} dy={0}>{date}</tspan>
        <tspan x={0} dy={12}>{time}</tspan>
      </text>
    </g>
  );
};

export const StationCharts: React.FC<StationChartsProps> = ({ 
  station,
  seasonYear,
  circuitId,
  geojsonId,
  selectedStartDate,
  selectedEndDate,
  circuitTimeZone,
  stationLat,
  stationLon,
  sessions = [],
  detailModalOpen,
  onDetailModalClose,
  hideLegend = false,
  chartHeight = 250,
  visibleCharts,
  disableAnimations = false,
}) => {
  const showChart = (id: string) => !visibleCharts || (visibleCharts as string[]).includes(id);

  const { theme } = useTheme();
  const { chartAnimationsEnabled: globalAnimations } = useChartAnimations();
  const chartAnimationsEnabled = disableAnimations ? false : globalAnimations;
  const { getField } = useObservationFields();
  const isDark = theme === 'dark';
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [circuitSvg, setCircuitSvg] = useState<{ path: string; viewBox: string; projection: CircuitProjection } | null>(null);

  const needsWindDirection = showChart('wind-direction');

  // Load circuit data for wind direction chart background
  useEffect(() => {
    if (!needsWindDirection) return;
    const loadCircuit = async () => {
      try {
        const circuitData = await circuitsService.getCircuitById(circuitId, geojsonId);
        if (circuitData?.geojson) {
          const result = geojsonToSVGPathAndBounds(circuitData.geojson as GeoJSONFeatureCollection);
          setCircuitSvg(result);
        }
      } catch (err) {
        console.error('Failed to load circuit for wind direction chart:', err);
      }
    };
    loadCircuit();
  }, [circuitId, geojsonId, needsWindDirection]);

  // Center the track layer on the station in SVG space; default centers the track
  const windChartTrackTranslate = useMemo(() => {
    if (!circuitSvg?.projection || stationLat == null || stationLon == null) {
      return { x: 0, y: 0 };
    }
    const { minLng, maxLat, lngScale, maxDim, svgWidth, svgHeight } = circuitSvg.projection;
    const sx = (stationLon - minLng) * lngScale / maxDim * 100;
    const sy = (maxLat - stationLat) / maxDim * 100;
    // Translate so the station is at the center of the SVG viewBox
    return { x: (svgWidth / 2) - sx, y: (svgHeight / 2) - sy };
  }, [circuitSvg?.projection, stationLat, stationLon]);


  // Get units from observation_fields table
  // Note: Wind fields are stored in m/s but displayed in km/h (converted in chartData)
  const temperatureUnit = getField('temperature')?.unit || '°C';
  const trackTemperatureUnit = getField('trackTemperature')?.unit || '°C';
  const humidityUnit = getField('humidity')?.unit || '%';
  const pressureUnit = getField('pressureSurfaceLevel')?.unit || 'hPa';
  const rainUnit = getField('rainIntensity')?.unit || getField('precipitationIntensity')?.unit || 'mm/h';
  // Wind is converted from m/s to km/h, so display unit is always km/h
  const windDisplayUnit = 'km/h';

  // Transform station data into chart-ready format
  const chartData = useMemo<ChartDataPoint[]>(() => {
    const rangeDurationMs = selectedEndDate.getTime() - selectedStartDate.getTime();
    const applyDownsampling = shouldDownsample(rangeDurationMs);

    const observationsToUse = (() => {
      if (!applyDownsampling) return station.observations;
      if (station.observations.length === 0) return [];
      const minMs = new Date(station.observations[0].reading_timestamp).getTime();
      const maxMs = new Date(station.observations[station.observations.length - 1].reading_timestamp).getTime();
      const bucketMs = getAutoBucketMs(rangeDurationMs);
      const buckets = getBucketStarts(minMs, maxMs, bucketMs);
      return buckets.map(bucketStartMs => {
        const bucketEndMs = bucketStartMs + bucketMs;
        return station.observations.find(obs => {
          const ms = new Date(obs.reading_timestamp).getTime();
          return ms >= bucketStartMs && ms < bucketEndMs;
        });
      }).filter((obs): obs is NonNullable<typeof obs> => obs != null);
    })();

    const data = observationsToUse.map(obs => {
      const datetime = new Date(obs.reading_timestamp);
      const timeStr = datetime.toLocaleTimeString('en-US', {
        timeZone: circuitTimeZone || undefined,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const dateStr = datetime.toLocaleDateString('en-US', {
        timeZone: circuitTimeZone || undefined,
        month: 'short',
        day: '2-digit',
      });
      const dataPoint: ChartDataPoint = {
        timestamp: obs.reading_timestamp,
        time: timeStr,
        date: dateStr,
        dateTimeLabel: `${dateStr}\n${timeStr}`,
      };

      // Map readings by name
      station.reading_config.forEach((config, index) => {
        const value = obs.readings[index];
        switch (config.name) {
          case 'temperature':
            dataPoint.airTemp = value;
            break;
          case 'trackTemperature':
            dataPoint.trackTemp = value;
            break;
          case 'windSpeed':
            // Convert from m/s to km/h
            dataPoint.windSpeed = value !== null ? value * 3.6 : value;
            break;
          case 'windGust':
            // Convert from m/s to km/h
            dataPoint.gusts = value !== null ? value * 3.6 : value;
            break;
          case 'humidity':
            dataPoint.humidity = value;
            break;
          case 'pressureSurfaceLevel':
            dataPoint.pressure = value;
            break;
          case 'rainIntensity':
          case 'precipitationIntensity':
            dataPoint.rain = value;
            break;
          case 'windDirection':
            dataPoint.windDirection = value;
            break;
        }
      });

      return dataPoint;
    });

    // Calculate interval for wind arrows (show ~20 arrows max to avoid clutter)
    const arrowInterval = Math.max(1, Math.floor(data.length / 20));
    
    // Calculate max wind value to determine arrow Y position
    const maxWind = Math.max(
      ...data.map(d => d.windSpeed ?? 0),
      ...data.map(d => d.gusts ?? 0)
    );
    // Position arrows at -15% of max wind value (below the chart area)
    const arrowYPosition = -(maxWind * 0.15);
    
    // Add windArrowY for arrow positioning (only at intervals to avoid clutter)
    return data.map((point, index) => ({
      ...point,
      // Set windArrowY to negative value only at intervals where we want to show arrows
      windArrowY: (index % arrowInterval === 0 && point.windDirection !== null && point.windDirection !== undefined) ? arrowYPosition : undefined,
    }));
  }, [station, circuitTimeZone, selectedStartDate, selectedEndDate]);

  // Calculate Y-axis domain for wind chart (includes space for arrows below)
  const windYDomain = useMemo(() => {
    if (!showChart('wind')) return [0, 1];
    const maxWind = Math.max(
      ...chartData.map(d => d.windSpeed ?? 0),
      ...chartData.map(d => d.gusts ?? 0)
    );
    return [-(maxWind * 0.2), maxWind * 1.1];
  }, [chartData, visibleCharts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pressure Y-axis domain: data-driven with padding (matches AllStationsComparison auto scale)
  const pressureYDomain = useMemo((): [number, number] => {
    if (!showChart('humidity-pressure')) return [0, 1];
    const values = chartData
      .map(d => d.pressure)
      .filter((v): v is number => v !== null && v !== undefined && !isNaN(v));
    if (values.length === 0) return [0, 1];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const padding = range > 0 ? range * 0.05 : Math.max(1, Math.abs(min) * 0.1);
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData, visibleCharts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate wind direction distribution
  const windDirectionData = useMemo<WindDirectionData[]>(() => {
    if (!needsWindDirection) return [];
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    // Buckets: [direction][speedCategory] - 5 speed categories per direction
    const buckets = directions.map(() => [0, 0, 0, 0, 0]);
    let totalCount = 0;

    chartData.forEach(point => {
      if (point.windDirection !== null && point.windDirection !== undefined) {
        const dirIndex = Math.floor(((point.windDirection + 11.25) % 360) / 22.5);
        const speed = point.windSpeed ?? 0;
        
        // Determine speed category
        let speedCategory = 4; // > 20 km/h
        if (speed < 5) speedCategory = 0;
        else if (speed < 10) speedCategory = 1;
        else if (speed < 15) speedCategory = 2;
        else if (speed < 20) speedCategory = 3;
        
        buckets[dirIndex][speedCategory]++;
        totalCount++;
      }
    });

    return directions.map((dir, index) => {
      const dirTotal = buckets[index].reduce((a, b) => a + b, 0);
      return {
        direction: dir,
        percentage: totalCount > 0 ? (dirTotal / totalCount) * 100 : 0,
        count: dirTotal,
        // Stacked values - each represents cumulative percentage up to that speed
        speed0to5: totalCount > 0 ? (buckets[index][0] / totalCount) * 100 : 0,
        speed5to10: totalCount > 0 ? ((buckets[index][0] + buckets[index][1]) / totalCount) * 100 : 0,
        speed10to15: totalCount > 0 ? ((buckets[index][0] + buckets[index][1] + buckets[index][2]) / totalCount) * 100 : 0,
        speed15to20: totalCount > 0 ? ((buckets[index][0] + buckets[index][1] + buckets[index][2] + buckets[index][3]) / totalCount) * 100 : 0,
        speed20plus: totalCount > 0 ? (dirTotal / totalCount) * 100 : 0,
      };
    });
  }, [chartData, needsWindDirection]);

  // Calculate statistics (skipped when legend is hidden since stats are only shown in the legend)
  const stats = useMemo(() => {
    if (hideLegend) {
      const zero = { min: 0, max: 0, avg: 0 };
      return { airTemp: zero, trackTemp: zero, windSpeed: zero, gusts: zero, humidity: zero, pressure: zero, rain: { ...zero, total: 0 } };
    }
    const calculateStats = (values: (number | null | undefined)[]) => {
      const validValues = values.filter((v): v is number => v !== null && v !== undefined && !isNaN(v));
      if (validValues.length === 0) return { min: 0, max: 0, avg: 0 };
      return {
        min: Math.min(...validValues),
        max: Math.max(...validValues),
        avg: validValues.reduce((a, b) => a + b, 0) / validValues.length,
      };
    };

    // Rain total accumulation: trapezoidal integration of intensity (mm/h) over time
    const calculateRainTotal = () => {
      if (chartData.length < 2) return 0;
      let total = 0;
      for (let i = 0; i < chartData.length - 1; i++) {
        const r0 = chartData[i].rain ?? 0;
        const r1 = chartData[i + 1].rain ?? 0;
        const t0 = new Date(chartData[i].timestamp).getTime();
        const t1 = new Date(chartData[i + 1].timestamp).getTime();
        const hours = (t1 - t0) / (1000 * 60 * 60);
        total += 0.5 * (r0 + r1) * hours;
      }
      return total;
    };

    const rainStats = calculateStats(chartData.map(d => d.rain));
    return {
      airTemp: calculateStats(chartData.map(d => d.airTemp)),
      trackTemp: calculateStats(chartData.map(d => d.trackTemp)),
      windSpeed: calculateStats(chartData.map(d => d.windSpeed)),
      gusts: calculateStats(chartData.map(d => d.gusts)),
      humidity: calculateStats(chartData.map(d => d.humidity)),
      pressure: calculateStats(chartData.map(d => d.pressure)),
      rain: { ...rainStats, total: calculateRainTotal() },
    };
  }, [chartData, hideLegend]);

  // Session boundary data for time-based charts (same logic as AllStationsComparison)
  const sessionBoundaries = useMemo(() => {
    if (sessions.length === 0 || chartData.length === 0) return [];

    return sessions.map((session) => {
      const sessionStartMs = new Date(session.start_time).getTime();
      const sessionEndMs = new Date(session.end_time).getTime();

      let startIndex = -1;
      let endIndex = -1;

      chartData.forEach((dataPoint, i) => {
        const pointTime = new Date(dataPoint.timestamp).getTime();
        if (pointTime >= sessionStartMs && pointTime <= sessionEndMs) {
          if (startIndex === -1) startIndex = i;
          endIndex = i;
        }
      });

      if (startIndex === -1 || endIndex === -1) return null;

      const startLabel = chartData[startIndex].dateTimeLabel;
      const extendedEndIndex = Math.min(endIndex + 1, chartData.length - 1);
      const endLabel = chartData[extendedEndIndex].dateTimeLabel;

      return { startLabel, endLabel, sessionName: session.session_name };
    }).filter(Boolean) as Array<{ startLabel: string; endLabel: string; sessionName: string }>;
  }, [sessions, chartData]);

  // Render session reference areas for a specific chart (yAxisId must match the chart's YAxis)
  const renderSessionAreas = (yAxisId?: string) => {
    return sessionBoundaries.map((boundary, index) => (
      <ReferenceArea
        key={`session-${index}`}
        x1={boundary.startLabel}
        x2={boundary.endLabel}
        {...(yAxisId ? { yAxisId } : {})}
        fill={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
        fillOpacity={1}
        stroke={isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}
        strokeWidth={1}
        ifOverflow="visible"
      >
        <Label
          value={boundary.sessionName}
          position="insideTop"
          fill={isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
          fontSize={10}
          fontWeight={500}
          offset={5}
        />
      </ReferenceArea>
    ));
  };

  const chartColors = {
    airTemp: '#ff7f0e',
    trackTemp: '#1f77b4',
    windSpeed: '#2ca02c',
    gusts: '#d62728',
    humidity: '#9467bd',
    pressure: '#8c564b',
    rain: '#1890ff',
    grid: isDark ? '#444' : '#ddd',
    text: isDark ? '#fff' : '#000',
  };

  const handleExportCSV = () => {
    // Generate filename with event and selected timeline range
    const formatDateForFilename = (date: Date) => {
      return date.toISOString()
        .replace(/:/g, '-')
        .replace(/\..+/, '')
        .replace('T', '_');
    };
    
    const startTime = formatDateForFilename(selectedStartDate);
    const endTime = formatDateForFilename(selectedEndDate);
    const filename = `${seasonYear}_${circuitId}_station${station.station_reference}_${startTime}_to_${endTime}.csv`;
    
    // Prepare CSV headers dynamically from reading_config with units from observation_fields
    const headers = [
      'Time',
      'Timestamp',
      ...station.reading_config.map(config => {
        const fieldUnit = getField(config.name)?.unit || '';
        return `${config.name}${fieldUnit ? ` (${fieldUnit})` : ''}`;
      }),
    ];

    const csvRows = [
      // Title row
      [`Ground Station ${station.station_reference} - Weather Observations`],
      [`Event: ${seasonYear} ${circuitId}`],
      [`Selected Time Range: ${selectedStartDate.toISOString()} to ${selectedEndDate.toISOString()}`],
      [`Total Observations: ${station.observations.length}`],
      [], // Empty row
      // Headers
      headers,
      // Data rows
      ...station.observations.map(obs => [
        new Date(obs.reading_timestamp).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        obs.reading_timestamp,
        ...obs.readings.map(value => {
          if (value === null || value === undefined) return '';
          // Format number with appropriate precision
          return typeof value === 'number' ? value.toFixed(2) : value;
        }),
      ]),
    ];

    // Convert to CSV string
    const csvContent = csvRows
      .map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
      .join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    if (onDetailModalClose) {
      onDetailModalClose();
    } else {
      setIsDetailModalOpen(false);
    }
  };

  const isModalOpen = detailModalOpen !== undefined ? detailModalOpen : isDetailModalOpen;

  // Prepare table data dynamically
  const tableData = useMemo(() => {
    return station.observations.map((obs, index) => {
      const row: Record<string, string | number | null> = {
        key: index,
        time: new Date(obs.reading_timestamp).toLocaleTimeString('en-US', {
          timeZone: circuitTimeZone || undefined,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        timestamp: obs.reading_timestamp,
      };
      
      // Add each reading as a column
      station.reading_config.forEach((config, idx) => {
        row[config.name] = obs.readings[idx];
      });
      
      return row;
    });
  }, [station, circuitTimeZone]);

  // Table columns - dynamically generated from reading_config with units from observation_fields
  const tableColumns: ColumnsType<Record<string, string | number | null>> = useMemo(() => {
    const columns: ColumnsType<Record<string, string | number | null>> = [
      {
        title: 'Time',
        dataIndex: 'time',
        key: 'time',
        fixed: 'left',
        width: 80,
      },
    ];
    
    // Add columns for each reading with units from observation_fields
    station.reading_config.forEach(config => {
      const fieldUnit = getField(config.name)?.unit || '';
      columns.push({
        title: `${config.name}${fieldUnit ? ` (${fieldUnit})` : ''}`,
        dataIndex: config.name,
        key: config.name,
        width: 120,
        render: (value) => {
          if (value === null || value === undefined) return '-';
          return typeof value === 'number' ? value.toFixed(2) : value;
        },
      });
    });
    
    return columns;
  }, [station.reading_config, getField]);

  return (
    <div
      style={{
        backgroundColor: isDark ? '#141414' : '#ffffff',
        borderRadius: '8px',
        border: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Charts */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '12px', flex: 1 }}>
        {/* Temperature Chart */}
        {showChart('temperature') && <div style={{ flex: '1 1 0', minWidth: '0' }}>
          <div className="chart-container">
            <h4 className="chart-title">Temperature ({temperatureUnit})</h4>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {renderSessionAreas()}
                <XAxis
                  dataKey="dateTimeLabel"
                  stroke={chartColors.text}
                  tick={(props) => <CustomXAxisTick {...props} textColor={chartColors.text} />}
                  interval="preserveStartEnd"
                />
                <YAxis stroke={chartColors.text} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    border: `1px solid ${chartColors.grid}`,
                  }}
                />
                {!hideLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
                <Line
                  type="monotone"
                  dataKey="airTemp"
                  stroke={chartColors.airTemp}
                  name={`Air (${temperatureUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
                <Line
                  type="monotone"
                  dataKey="trackTemp"
                  stroke={chartColors.trackTemp}
                  name={`Track (${trackTemperatureUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
              </LineChart>
            </ResponsiveContainer>
            {!hideLegend && (
              <div className="chart-stats">
                <div>Air: Min {stats.airTemp.min.toFixed(1)} Max {stats.airTemp.max.toFixed(1)} Avg {stats.airTemp.avg.toFixed(1)} {temperatureUnit}</div>
                <div>Track: Min {stats.trackTemp.min.toFixed(1)} Max {stats.trackTemp.max.toFixed(1)} Avg {stats.trackTemp.avg.toFixed(1)} {trackTemperatureUnit}</div>
              </div>
            )}
          </div>
        </div>}

        {/* Wind Chart */}
        {showChart('wind') && <div style={{ flex: '1 1 0', minWidth: '0' }}>
          <div className="chart-container">
            <h4 className="chart-title">Wind ({windDisplayUnit})</h4>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {renderSessionAreas()}
                <XAxis
                  dataKey="dateTimeLabel"
                  stroke={chartColors.text}
                  tick={(props) => <CustomXAxisTick {...props} textColor={chartColors.text} />}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke={chartColors.text} 
                  tick={{ fontSize: 10 }} 
                  domain={windYDomain}
                  tickFormatter={(value) => value < 0 ? '' : value.toFixed(0)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    border: `1px solid ${chartColors.grid}`,
                  }}
                  formatter={(value, name) => {
                    const seriesName = String(name ?? '');
                    if (seriesName === 'windArrowY') return null;
                    if (typeof value === 'number') {
                      return [value.toFixed(1), seriesName];
                    }
                    return [value, seriesName];
                  }}
                  filterNull={true}
                />
                {!hideLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
                <Line
                  type="linear"
                  dataKey="windSpeed"
                  stroke={chartColors.windSpeed}
                  name={`Speed (${windDisplayUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
                <Line
                  type="linear"
                  dataKey="gusts"
                  stroke={chartColors.gusts}
                  name={`Gusts (${windDisplayUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
                {/* Wind direction arrows at the bottom of the chart */}
                <Scatter
                  dataKey="windArrowY"
                  shape={<WindArrow />}
                  legendType="none"
                  isAnimationActive={chartAnimationsEnabled}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {!hideLegend && (
              <>
                <div className="chart-stats">
                  <div>Speed: Min {stats.windSpeed.min.toFixed(1)} Max {stats.windSpeed.max.toFixed(1)} Avg {stats.windSpeed.avg.toFixed(1)} {windDisplayUnit}</div>
                  <div>Gusts: Min {stats.gusts.min.toFixed(1)} Max {stats.gusts.max.toFixed(1)} Avg {stats.gusts.avg.toFixed(1)} {windDisplayUnit}</div>
                </div>
                {/* Wind speed color legend for direction arrows - styled to match chart-stats */}
                <div className="chart-stats" style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: '8px', 
                  justifyContent: 'flex-start',
                }}>
                  {windSpeedColors.map(({ color, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="10" height="10" viewBox="-4 -7 8 14">
                        <path d="M0,-6 L3,4 L0,2 L-3,4 Z" fill={color} stroke="#333" strokeWidth={0.5} />
                      </svg>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>}

        {/* Humidity/Pressure Chart */}
        {showChart('humidity-pressure') && <div style={{ flex: '1 1 0', minWidth: '0' }}>
          <div className="chart-container">
            <h4 className="chart-title">Humidity ({humidityUnit}) / Pressure ({pressureUnit})</h4>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {renderSessionAreas('left')}
                <XAxis
                  dataKey="dateTimeLabel"
                  stroke={chartColors.text}
                  tick={(props) => <CustomXAxisTick {...props} textColor={chartColors.text} />}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="left"
                  stroke={chartColors.text}
                  tick={{ fontSize: 10 }}
                  label={{ value: `Humidity (${humidityUnit})`, angle: -90, position: 'insideLeft', fontSize: 10 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={chartColors.text}
                  tick={{ fontSize: 10 }}
                  domain={pressureYDomain}
                  label={{ value: `Pressure (${pressureUnit})`, angle: 90, position: 'insideRight', fontSize: 10 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    border: `1px solid ${chartColors.grid}`,
                  }}
                />
                {!hideLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="humidity"
                  stroke={chartColors.humidity}
                  name={`Humidity (${humidityUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="pressure"
                  stroke={chartColors.pressure}
                  name={`Pressure (${pressureUnit})`}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={chartAnimationsEnabled}
                />
              </LineChart>
            </ResponsiveContainer>
            {!hideLegend && (
              <div className="chart-stats">
                <div>Humidity: Min {stats.humidity.min.toFixed(0)} Max {stats.humidity.max.toFixed(0)} Avg {stats.humidity.avg.toFixed(1)} {humidityUnit}</div>
                <div>Pressure: Min {stats.pressure.min.toFixed(1)} Max {stats.pressure.max.toFixed(1)} Avg {stats.pressure.avg.toFixed(1)} {pressureUnit}</div>
              </div>
            )}
          </div>
        </div>}

        {/* Rain Chart */}
        {showChart('rain') && <div style={{ flex: '1 1 0', minWidth: '0' }}>
          <div className="chart-container">
            <h4 className="chart-title">Rain ({rainUnit})</h4>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {renderSessionAreas()}
                <XAxis
                  dataKey="dateTimeLabel"
                  stroke={chartColors.text}
                  tick={(props) => <CustomXAxisTick {...props} textColor={chartColors.text} />}
                  interval="preserveStartEnd"
                />
                <YAxis stroke={chartColors.text} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    border: `1px solid ${chartColors.grid}`,
                  }}
                />
                <Bar dataKey="rain" fill={chartColors.rain} name={`Rain (${rainUnit})`} isAnimationActive={chartAnimationsEnabled} />
              </BarChart>
            </ResponsiveContainer>
            {!hideLegend && (
              <div className="chart-stats">
                <div>Avg: {stats.rain.avg.toFixed(2)} {rainUnit} | Total: {stats.rain.total.toFixed(2)} mm</div>
              </div>
            )}
          </div>
        </div>}

        {/* Wind Direction Chart */}
        {showChart('wind-direction') && <div style={{ flex: '1 1 0', minWidth: '0' }}>
          <div className="chart-container">
            <h4 className="chart-title">Wind Direction (%)</h4>
            <div style={{ position: 'relative', width: '100%', height: chartHeight, minWidth: 0 }}>
              {/* Circuit background - centred on this weather station when lat/lon provided */}
              {circuitSvg && (
                <svg
                  viewBox={circuitSvg.viewBox}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '55%',
                    height: '55%',
                    opacity: 0.25,
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <g transform={`translate(${windChartTrackTranslate.x}, ${windChartTrackTranslate.y})`}>
                    <path
                      d={circuitSvg.path}
                      fill="none"
                      stroke={isDark ? '#4a90d9' : '#0E447D'}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              )}
              {/* Radar chart with stacked speed-colored layers */}
              <ResponsiveContainer width="100%" height={chartHeight} minWidth={0}>
                <RadarChart data={windDirectionData}>
                  <PolarGrid stroke={chartColors.grid} />
                  <PolarAngleAxis
                    dataKey="direction"
                    stroke={chartColors.text}
                    tick={{ fontSize: 10 }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 'auto']}
                    stroke={chartColors.text}
                    tick={{ fontSize: 10 }}
                  />
                  {/* Stacked radar layers - render in reverse order (largest first) */}
                  <Radar
                    name="> 20 km/h"
                    dataKey="speed20plus"
                    stroke="#FF4444"
                    fill="#FF4444"
                    fillOpacity={0.8}
                    isAnimationActive={chartAnimationsEnabled}
                  />
                  <Radar
                    name="15-20 km/h"
                    dataKey="speed15to20"
                    stroke="#FFA500"
                    fill="#FFA500"
                    fillOpacity={0.8}
                    isAnimationActive={chartAnimationsEnabled}
                  />
                  <Radar
                    name="10-15 km/h"
                    dataKey="speed10to15"
                    stroke="#FFD700"
                    fill="#FFD700"
                    fillOpacity={0.8}
                    isAnimationActive={chartAnimationsEnabled}
                  />
                  <Radar
                    name="5-10 km/h"
                    dataKey="speed5to10"
                    stroke="#32CD32"
                    fill="#32CD32"
                    fillOpacity={0.8}
                    isAnimationActive={chartAnimationsEnabled}
                  />
                  <Radar
                    name="< 5 km/h"
                    dataKey="speed0to5"
                    stroke="#00CED1"
                    fill="#00CED1"
                    fillOpacity={0.8}
                    isAnimationActive={chartAnimationsEnabled}
                  />
                  <Tooltip
                    content={(props) => <WindDirectionTooltip {...props} isDark={isDark} />}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {!hideLegend && (
              <div className="chart-stats" style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '8px', 
                justifyContent: 'center',
              }}>
                {windSpeedColors.map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="10" height="10" viewBox="-4 -7 8 14">
                      <path d="M0,-6 L3,4 L0,2 L-3,4 Z" fill={color} stroke="#333" strokeWidth={0.5} />
                    </svg>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>}
      </div>

      {/* Detail Modal */}
      <Modal
        title={`Ground station ${station.station_reference} - Detailed Data`}
        open={isModalOpen}
        onCancel={handleCloseModal}
        width="90%"
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Close
          </Button>,
          <Button key="export" type="primary" icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Export CSV
          </Button>,
        ]}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>Total Observations:</strong> {station.observation_count} | 
          <strong style={{ marginLeft: 16 }}>Showing:</strong> {tableData.length} records
        </div>
        <Table
          columns={tableColumns}
          dataSource={tableData}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100', '200'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} observations`,
          }}
          size="small"
        />
          </Modal>
    </div>
  );
};

